import React from "react";
import { createPortal } from "react-dom";

/**
 * "Unsupported File Format" modal.
 *
 * Shown by the Brand Kit drag-and-drop zones (fonts / brand images /
 * logos / icons) when the user tries to upload a file whose
 * extension isn't on the section's allow-list. The card mirrors the
 * BrowserWarningModal's layout — same 555px card, same Proxima
 * Nova typography, same 8px-corner buttons — so the warning system
 * reads as one design family.
 *
 * The only action is the centered "Upload New File" button which
 * just dismisses the modal. The user retries with a different file
 * via the same drag-and-drop / file picker; we don't try to "re-open"
 * the file dialog automatically because there's no portable way to
 * do that without a fresh user gesture.
 */

const FONT_STACK = '"Inter", "Nunito Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

interface UnsupportedFileFormatModalProps {
  open: boolean;
  onClose: () => void;
  /** Allowed extensions, e.g. [".png", ".pdf"]. Rendered inline in
   *  the body copy with each extension bolded. The list separator
   *  is a comma, except the last item uses "or" for Oxford-style
   *  readability. */
  acceptedFormats: string[];
}

const renderFormatsList = (formats: string[]): React.ReactNode => {
  if (formats.length === 0) return null;
  if (formats.length === 1) return <strong>{formats[0]}</strong>;
  if (formats.length === 2) {
    return (
      <>
        <strong>{formats[0]}</strong> or <strong>{formats[1]}</strong>
      </>
    );
  }
  // 3+: "a, b, c, or d"
  const head = formats.slice(0, -1);
  const last = formats[formats.length - 1];
  return (
    <>
      {head.map((f, i) => (
        <React.Fragment key={f}>
          <strong>{f}</strong>
          {i < head.length - 1 ? ", " : ""}
        </React.Fragment>
      ))}
      {", or "}
      <strong>{last}</strong>
    </>
  );
};

export const UnsupportedFileFormatModal: React.FC<UnsupportedFileFormatModalProps> = ({
  open,
  onClose,
  acceptedFormats,
}) => {
  if (!open) return null;

  const modal = (
    <div
      onClick={(e) => {
        // Backdrop click also dismisses — same affordance as the
        // central button, since there's no "destructive" path to
        // protect against.
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(0, 8, 30, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: FONT_STACK,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          width: 555,
          maxWidth: "100%",
          padding: "24px 24px 32px 24px",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 24,
          borderRadius: 8,
          border: "1px solid #E2E2FC",
          background: "#FFF",
          boxShadow: "0 2px 16px 0 rgba(37, 59, 97, 0.10)",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "#222336",
              textAlign: "center",
              fontFamily: FONT_STACK,
              fontSize: 24,
              fontWeight: 700,
              lineHeight: "normal",
            }}
          >
            Unsupported File Format
          </h2>
          <p
            style={{
              margin: 0,
              color: "#222336",
              textAlign: "center",
              fontFamily: FONT_STACK,
              fontSize: 16,
              fontWeight: 400,
              lineHeight: "22px",
            }}
          >
            This file type is not supported. Please upload {renderFormatsList(acceptedFormats)}.
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          data-cypress-id="unsupported-file-format-modal-upload-new-file-button"
          style={{
            display: "flex",
            minHeight: 40,
            maxHeight: 40,
            padding: "0 24px",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            borderRadius: 8,
            border: "none",
            // Mirrors the BrowserWarningModal "Get Google Chrome"
            // primary CTA — same fill, height, radius and typography.
            background: "#EE4360",
            color: "#FFF",
            textAlign: "center",
            fontFamily: FONT_STACK,
            fontSize: 16,
            fontWeight: 700,
            lineHeight: "120%",
            cursor: "pointer",
            boxSizing: "border-box",
          }}
        >
          Upload New File
        </button>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
};

export default UnsupportedFileFormatModal;
